import dayjs from 'dayjs';
import {
  CustomFieldEntity,
  CustomFieldOptionsSource,
  CustomFieldStudentAccess,
  CustomFieldType,
  FormFieldType,
  type CreateCustomFieldInput,
  type UpdateCustomFieldInput,
} from '@/types/composition-functions';
import type {
  CustomFieldFieldsFragment,
  CustomFieldValueFieldsFragment,
} from '@/types/operations';
import { allLgas, lgaBelongsToState, lgasForState, type NigerianStateOption } from '@/utils/nigerianStates';

/**
 * School-defined custom fields (#574): the pure logic behind the settings
 * screen, the student profile card and the form-builder mapping, kept out of
 * the components so it can be unit-tested.
 */

export type CustomFieldDefinition = CustomFieldValueFieldsFragment['field'];

/** A value as the edit form holds it, per field type. */
export type CustomFieldInputValue = string | number | boolean | Date | null;

export const CUSTOM_FIELD_TYPE_OPTIONS: ReadonlyArray<{ label: string; value: CustomFieldType; hint: string }> = [
  { label: 'Short text', value: CustomFieldType.Text, hint: 'One line, up to 255 characters' },
  { label: 'Long text', value: CustomFieldType.Textarea, hint: 'Several lines, up to 5,000 characters' },
  { label: 'Number', value: CustomFieldType.Number, hint: 'Digits only, decimals allowed' },
  { label: 'Date', value: CustomFieldType.Date, hint: 'Picked from a calendar' },
  { label: 'Dropdown', value: CustomFieldType.Select, hint: 'One choice from a list you define' },
  { label: 'Yes / No', value: CustomFieldType.Checkbox, hint: 'A single tick box' },
];

/**
 * Per-entity wording for the settings screen and dialog (#574 phase 4).
 * Only students can be filled from admission forms, so only their key hint
 * mentions it.
 */
export const CUSTOM_FIELD_ENTITY_OPTIONS: ReadonlyArray<{
  value: CustomFieldEntity;
  label: string;
  /** The settings page's ?entity= value, so a link can open one record type. */
  slug: string;
  /** Singular noun for counts, e.g. "3 staff member(s)". */
  noun: string;
  heading: string;
  description: string;
  keyHint: string;
}> = [
  {
    value: CustomFieldEntity.Student,
    label: 'Students',
    slug: 'students',
    noun: 'student',
    heading: 'Student fields',
    description: 'The details your school records about each student. Add your own, such as blood group, NIN or previous school: '
      + 'they appear in the "Additional information" card on every student profile, and admission forms can fill them in. '
      + '"Students" sets what students see and fill in on their own profile.',
    keyHint: 'Used by admission forms to fill this field.',
  },
  {
    value: CustomFieldEntity.Staff,
    label: 'Staff',
    slug: 'staff',
    noun: 'staff member',
    heading: 'Staff Custom Fields',
    description: 'Add the HR details your school records about each staff member, such as staff number, qualification, '
      + 'date employed or grade level. They appear in the "Additional information" card on every staff profile.',
    keyHint: 'A stable identifier for this field.',
  },
  {
    value: CustomFieldEntity.Guardian,
    label: 'Guardians',
    slug: 'guardians',
    noun: 'guardian',
    heading: 'Guardian Custom Fields',
    description: 'Add the extra details your school records about each parent or guardian, such as occupation, employer '
      + 'or address. They appear in the "Additional information" card on every guardian\'s page.',
    keyHint: 'A stable identifier for this field.',
  },
];

export function customFieldEntityCopy(entity: CustomFieldEntity) {
  return CUSTOM_FIELD_ENTITY_OPTIONS.find(o => o.value === entity) ?? CUSTOM_FIELD_ENTITY_OPTIONS[0]!;
}

/** The record type a settings link names (?entity=staff); students when it names none. */
export function customFieldEntityFromSlug(slug: unknown): CustomFieldEntity {
  return CUSTOM_FIELD_ENTITY_OPTIONS.find(o => o.slug === slug)?.value ?? CustomFieldEntity.Student;
}

export function customFieldTypeLabel(type: CustomFieldType | string | null | undefined): string {
  return CUSTOM_FIELD_TYPE_OPTIONS.find(o => o.value === type)?.label ?? String(type ?? '');
}

// ── Dropdowns whose choices come from a built-in list ───────────────────────

/** Sentinel for "the school's own options" in the dialog's choices picker (null cannot be a Select value). */
export const OWN_OPTIONS_LIST = 'own';
export type CustomFieldChoicesValue = CustomFieldOptionsSource | typeof OWN_OPTIONS_LIST;

export const CUSTOM_FIELD_CHOICES_OPTIONS: ReadonlyArray<{ label: string; value: CustomFieldChoicesValue; hint: string }> = [
  { label: 'Your own list', value: OWN_OPTIONS_LIST, hint: 'Type the choices yourself' },
  { label: 'Nigerian states', value: CustomFieldOptionsSource.NigerianStates, hint: 'The 36 states and the FCT' },
  { label: 'Nigerian LGAs', value: CustomFieldOptionsSource.NigerianLgas, hint: 'Local government areas, optionally following a state field' },
];

/**
 * How a dropdown's choices are described in the settings list: "3 options",
 * "Nigerian states", or "Nigerian LGAs, follows State of Origin". Null for
 * every other type.
 */
export function customFieldChoicesSummary(
  field: {
    type: CustomFieldType;
    options?: ReadonlyArray<unknown>;
    options_source?: CustomFieldOptionsSource | null;
    depends_on?: string | null;
  },
  fields: ReadonlyArray<{ key: string; label: string }> = [],
): string | null {
  if (field.type !== CustomFieldType.Select) return null;
  if (field.options_source === CustomFieldOptionsSource.NigerianStates) return 'Nigerian states';
  if (field.options_source === CustomFieldOptionsSource.NigerianLgas) {
    if (!field.depends_on) return 'Nigerian LGAs';
    const parent = fields.find(f => f.key === field.depends_on)?.label ?? field.depends_on;
    return `Nigerian LGAs, follows ${parent}`;
  }
  const count = field.options?.length ?? 0;
  return `${count} option${count === 1 ? '' : 's'}`;
}

/** "Dropdown (Nigerian states)", "Short text", … for a field or a template field. */
export function customFieldKindLabel(
  field: Parameters<typeof customFieldChoicesSummary>[0],
  fields: ReadonlyArray<{ key: string; label: string }> = [],
): string {
  const choices = customFieldChoicesSummary(field, fields);
  return choices ? `${customFieldTypeLabel(field.type)} (${choices})` : customFieldTypeLabel(field.type);
}

// ── Templates ───────────────────────────────────────────────────────────────

/** What applying a template would do, as its button label. */
export function templateApplyLabel(template: { applied: boolean; fields: ReadonlyArray<{ exists: boolean }> }): string {
  if (template.applied) return 'Added';
  const missing = template.fields.filter(f => !f.exists).length;
  if (missing === 0) return 'Turn retired fields back on';
  const noun = `field${missing === 1 ? '' : 's'}`;
  return missing === template.fields.length ? `Add ${missing} ${noun}` : `Add ${missing} missing ${noun}`;
}

/** Toast text after applying a template: the labels added, or why nothing changed. */
export function templateAppliedMessage(added: ReadonlyArray<{ label: string }>): string {
  if (added.length === 0) return 'These fields are already set up, so nothing changed.';
  return `Added ${added.map(f => f.label).join(', ')}.`;
}

/**
 * The choices a list-sourced dropdown offers: every state, the LGAs of the
 * state chosen in the field it follows, or every LGA when it follows none.
 * Empty for a dropdown with the school's own options.
 */
export function sourcedChoices(
  field: Pick<CustomFieldDefinition, 'options_source' | 'depends_on'>,
  states: NigerianStateOption[],
  parentValue?: CustomFieldInputValue,
): string[] {
  if (field.options_source === CustomFieldOptionsSource.NigerianStates) {
    return states.map(s => s.name);
  }
  if (field.options_source === CustomFieldOptionsSource.NigerianLgas) {
    if (!field.depends_on) return allLgas(states);
    return typeof parentValue === 'string' ? lgasForState(states, parentValue) : [];
  }
  return [];
}

/**
 * Choices as dropdown options, keeping a saved value that is not on the list
 * (e.g. a migrated state of origin typed for a non-Nigerian student) so
 * opening the form never silently erases it.
 */
export function choicesWithCurrent(choices: string[], current: CustomFieldInputValue | undefined): Array<{ label: string; value: string }> {
  const options = choices.map(c => ({ label: c, value: c }));
  if (typeof current === 'string' && current.trim() !== '' && !choices.includes(current)) {
    return [{ label: current, value: current }, ...options];
  }
  return options;
}

/** True while a dropdown that follows another field has nothing to follow yet. */
export function awaitingParentChoice(
  field: Pick<CustomFieldDefinition, 'options_source' | 'depends_on'>,
  parentValue: CustomFieldInputValue | undefined,
): boolean {
  return field.options_source === CustomFieldOptionsSource.NigerianLgas
    && !!field.depends_on
    && !(typeof parentValue === 'string' && parentValue.trim() !== '');
}

/**
 * Keys of the dependent fields to clear after the user changes the listed
 * parent fields: an LGA that is not in the newly chosen state (or whose state
 * was cleared). Nothing is cleared before the state list has loaded.
 */
export function dependentsToClear(
  entries: ReadonlyArray<Pick<CustomFieldValueFieldsFragment, 'field'>>,
  model: Record<string, CustomFieldInputValue>,
  changedParents: ReadonlyArray<string>,
  states: NigerianStateOption[],
): string[] {
  if (states.length === 0) return [];
  return entries
    .map(e => e.field)
    .filter(f => f.options_source === CustomFieldOptionsSource.NigerianLgas && !!f.depends_on && changedParents.includes(f.depends_on))
    .filter((f) => {
      const value = model[f.key];
      if (typeof value !== 'string' || value.trim() === '') return false;
      const parent = model[f.depends_on as string];
      return !lgaBelongsToState(states, typeof parent === 'string' ? parent : null, value);
    })
    .map(f => f.key);
}

/** The Nigerian-states dropdowns an LGA dropdown can follow, excluding the field being edited. */
export function stateFieldChoices(
  fields: ReadonlyArray<Pick<CustomFieldFieldsFragment, 'key' | 'label' | 'type' | 'options_source' | 'is_active'>>,
  editingKey?: string | null,
): Array<{ key: string; label: string }> {
  return fields
    .filter(f => f.type === CustomFieldType.Select && f.options_source === CustomFieldOptionsSource.NigerianStates && f.key !== editingKey)
    .map(f => ({ key: f.key, label: f.is_active ? f.label : `${f.label} (retired)` }));
}

/** Keys of the fields other fields follow, for watching parent changes. */
export function parentFieldKeys(entries: ReadonlyArray<Pick<CustomFieldValueFieldsFragment, 'field'>>): string[] {
  return [...new Set(entries.map(e => e.field.depends_on).filter((k): k is string => !!k))];
}

/**
 * The key the backend derives when none is typed: lowercase, underscores,
 * starting with a letter. Shown as a preview while the admin types a label.
 */
export function customFieldKeyFromLabel(label: string): string {
  let key = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 63);

  if (key !== '' && !/^[a-z]/.test(key)) {
    key = `field_${key}`;
  }

  return key.slice(0, 63).replace(/_+$/, '');
}

export const CUSTOM_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;

/**
 * Text for the key hint while the admin types a label. A label with no
 * Latin letters or digits (e.g. "血型") derives no key, so say the backend
 * will generate one instead of promising an empty key.
 */
export function customFieldKeyPreview(label: string): string {
  const key = customFieldKeyFromLabel(label);
  return key === '' ? 'will be generated' : `"${key}"`;
}

/** Human-readable value for the profile card. */
export function formatCustomFieldValue(field: Pick<CustomFieldDefinition, 'type' | 'options'>, value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  switch (field.type) {
  case CustomFieldType.Checkbox:
    return value === '1' ? 'Yes' : 'No';
  case CustomFieldType.Select:
    return field.options.find(o => o.value === value)?.label ?? value;
  case CustomFieldType.Date:
    return dayjs(value).isValid() ? dayjs(value).format('MMMM D, YYYY') : value;
  default:
    return value;
  }
}

/** Stored text → the value an input component binds to. */
export function toInputValue(field: Pick<CustomFieldDefinition, 'type'>, value: string | null | undefined): CustomFieldInputValue {
  // An unanswered yes/no stays null so it is told apart from a saved "No".
  if (value === null || value === undefined || value === '') {
    return null;
  }

  switch (field.type) {
  case CustomFieldType.Checkbox:
    return value === '1' || value === 'true';
  case CustomFieldType.Number:
    return Number(value);
  case CustomFieldType.Date:
    // dayjs reads a date-only string as a local calendar date; new Date()
    // would read it as UTC and show the previous day at UTC+1 (WAT).
    return dayjs(value).toDate();
  default:
    return value;
  }
}

/** Input value → the text the API stores. null clears the value. */
export function fromInputValue(field: Pick<CustomFieldDefinition, 'type'>, value: CustomFieldInputValue): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  switch (field.type) {
  case CustomFieldType.Checkbox:
    return value ? 'true' : 'false';
  case CustomFieldType.Date:
    return value instanceof Date ? dayjs(value).format('YYYY-MM-DD') : String(value);
  case CustomFieldType.Number:
    return typeof value === 'number' && Number.isNaN(value) ? null : String(value);
  default: {
    const text = String(value).trim();
    return text === '' ? null : text;
  }
  }
}

/** Edit-form model keyed by field key. */
export function buildEditModel(values: CustomFieldValueFieldsFragment[]): Record<string, CustomFieldInputValue> {
  return Object.fromEntries(values.map(v => [v.field.key, toInputValue(v.field, v.value)]));
}

/**
 * Blank entries for a record that does not exist yet (admit student, new
 * guardian): every active field with no stored value.
 */
export function entriesFromDefinitions(
  fields: ReadonlyArray<CustomFieldDefinition>,
): CustomFieldValueFieldsFragment[] {
  return fields.map(field => ({ value: null, field }));
}

/**
 * Payload for update*CustomFieldValues: the fields whose value changed, so a
 * save never rewrites untouched values — plus every REQUIRED yes/no that has
 * no stored answer, sent as "No". An unticked box on a required yes/no is a
 * real answer; leaving it out would leave the field blank and the backend
 * would reject the save as missing.
 */
export function buildValuesPayload(
  values: CustomFieldValueFieldsFragment[],
  model: Record<string, CustomFieldInputValue>,
): Array<{ field_id: string; value: string | null }> {
  return values
    .map(v => {
      let next = fromInputValue(v.field, model[v.field.key] ?? null);
      if (next === null && v.field.type === CustomFieldType.Checkbox && v.field.is_required) {
        next = 'false';
      }
      return { entry: v, next };
    })
    .filter(({ entry, next }) => normaliseStored(entry.field, entry.value) !== normaliseStored(entry.field, next))
    .map(({ entry, next }) => ({ field_id: entry.field.id, value: next }));
}

/** Whether two input values store the same text: 12.5 and 12.50, a ticked box and "1". */
export function sameInputValue(field: Pick<CustomFieldDefinition, 'type'>, a: CustomFieldInputValue, b: CustomFieldInputValue): boolean {
  return normaliseStored(field, fromInputValue(field, a ?? null)) === normaliseStored(field, fromInputValue(field, b ?? null));
}

function normaliseStored(field: Pick<CustomFieldDefinition, 'type'>, value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (field.type === CustomFieldType.Checkbox) {
    return value === '1' || value === 'true' ? '1' : '0';
  }
  return value;
}

/**
 * Required fields left blank, keyed by field key with the same message the
 * backend sends, so the dialog can stop the save and point at the field.
 * A required yes/no is never blank — unticked means "No".
 */
export function missingRequiredErrors(
  values: CustomFieldValueFieldsFragment[],
  model: Record<string, CustomFieldInputValue>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const { field } of values) {
    if (!field.is_required || field.type === CustomFieldType.Checkbox) continue;
    if (fromInputValue(field, model[field.key] ?? null) === null) {
      errors[field.key] = `${field.label} is required.`;
    }
  }
  return errors;
}

/**
 * What the "Additional information" card shows. Loading and errors take
 * priority; with no fields, only people who can create fields get the
 * "set up" prompt — for everyone else the card stays out of the way.
 */
export function customFieldCardState(input: {
  loading: boolean;
  failed: boolean;
  count: number;
  canSetUp: boolean;
}): 'loading' | 'error' | 'ready' | 'empty' | 'hidden' {
  if (input.count > 0) return 'ready';
  if (input.loading) return 'loading';
  if (input.failed) return 'error';
  return input.canSetUp ? 'empty' : 'hidden';
}

/** Inline-error key the backend uses for a field's value. */
export function valueErrorKey(fieldKey: string): string {
  return `values.${fieldKey}`;
}

// ── Settings-screen form ────────────────────────────────────────────────────

// @allow-handwritten editable dialog state (non-null strings, blank option rows); buildCreateInput/buildUpdateInput map it to the generated inputs
export interface CustomFieldFormModel {
  label: string;
  key: string;
  type: CustomFieldType;
  options: Array<{ label: string; value: string }>;
  /** Dropdown only: a built-in list instead of `options`. */
  options_source: CustomFieldOptionsSource | null;
  /** Nigerian LGAs only: key of the state field this one follows. */
  depends_on: string | null;
  is_required: boolean;
  is_active: boolean;
  description: string;
  /** Student fields only: whether students see it on their profile and may fill it in. */
  student_access: CustomFieldStudentAccess;
}

export function emptyCustomFieldForm(): CustomFieldFormModel {
  return {
    label: '',
    key: '',
    type: CustomFieldType.Text,
    options: [{ label: '', value: '' }],
    options_source: null,
    depends_on: null,
    is_required: false,
    is_active: true,
    description: '',
    student_access: CustomFieldStudentAccess.Hidden,
  };
}

export function customFieldFormFrom(field: CustomFieldFieldsFragment): CustomFieldFormModel {
  return {
    label: field.label,
    key: field.key,
    type: field.type,
    options: field.options.length ? field.options.map(o => ({ label: o.label, value: o.value })) : [{ label: '', value: '' }],
    options_source: field.options_source ?? null,
    depends_on: field.depends_on ?? null,
    is_required: field.is_required,
    is_active: field.is_active,
    description: field.description ?? '',
    student_access: field.student_access,
  };
}

/** Options to send: blank rows dropped; only a dropdown with its own list carries any. */
export function cleanOptions(form: CustomFieldFormModel): Array<{ label: string; value?: string }> | undefined {
  if (form.type !== CustomFieldType.Select || form.options_source) {
    return undefined;
  }
  return form.options
    .map(o => ({ label: o.label.trim(), value: o.value.trim() }))
    .filter(o => o.label !== '')
    .map(o => (o.value ? o : { label: o.label }));
}

/** The list source to send: only a dropdown has one. */
function sourceFor(form: CustomFieldFormModel): CustomFieldOptionsSource | null {
  return form.type === CustomFieldType.Select ? form.options_source : null;
}

/** The followed field to send: only a Nigerian LGAs dropdown follows one. */
function dependsOnFor(form: CustomFieldFormModel): string | null {
  return sourceFor(form) === CustomFieldOptionsSource.NigerianLgas ? (form.depends_on || null) : null;
}

export function buildCreateInput(form: CustomFieldFormModel, entity: CustomFieldEntity): CreateCustomFieldInput {
  return {
    entity,
    label: form.label.trim(),
    key: form.key.trim() || undefined,
    type: form.type,
    options: cleanOptions(form),
    options_source: sourceFor(form),
    depends_on: dependsOnFor(form),
    is_required: form.is_required,
    is_active: form.is_active,
    description: form.description.trim() || null,
    student_access: entity === CustomFieldEntity.Student ? form.student_access : CustomFieldStudentAccess.Hidden,
  };
}

export function buildUpdateInput(form: CustomFieldFormModel): UpdateCustomFieldInput {
  return {
    label: form.label.trim(),
    type: form.type,
    options: cleanOptions(form),
    options_source: sourceFor(form),
    depends_on: dependsOnFor(form),
    is_required: form.is_required,
    is_active: form.is_active,
    description: form.description.trim() || null,
    student_access: form.student_access,
  };
}

/** Move one field up or down; returns the new id order for reorderCustomFields. */
export function moveFieldOrder(ids: string[], id: string, direction: -1 | 1): string[] {
  const index = ids.indexOf(id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ids.length) {
    return ids;
  }
  const next = [...ids];
  next.splice(index, 1);
  next.splice(target, 0, id);
  return next;
}

// ── Admission form builder: maps_to "custom:<key>" ──────────────────────────

export const CUSTOM_FIELD_TARGET_PREFIX = 'custom:';

/** Which custom field types each form field type can feed. */
const COMPATIBLE_CUSTOM_TYPES: Partial<Record<FormFieldType, CustomFieldType[]>> = {
  [FormFieldType.Text]: [CustomFieldType.Text, CustomFieldType.Textarea, CustomFieldType.Number],
  [FormFieldType.Textarea]: [CustomFieldType.Textarea, CustomFieldType.Text],
  [FormFieldType.Email]: [CustomFieldType.Text],
  [FormFieldType.Number]: [CustomFieldType.Number, CustomFieldType.Text],
  [FormFieldType.Date]: [CustomFieldType.Date, CustomFieldType.Text],
  [FormFieldType.Select]: [CustomFieldType.Select, CustomFieldType.Text, CustomFieldType.Checkbox],
  [FormFieldType.Radio]: [CustomFieldType.Select, CustomFieldType.Text, CustomFieldType.Checkbox],
};

export function isCustomFieldTarget(value: string | null | undefined): boolean {
  return typeof value === 'string'
    && value.startsWith(CUSTOM_FIELD_TARGET_PREFIX)
    && CUSTOM_FIELD_KEY_PATTERN.test(value.slice(CUSTOM_FIELD_TARGET_PREFIX.length));
}

export function canMapToCustomField(formFieldType: FormFieldType): boolean {
  return (COMPATIBLE_CUSTOM_TYPES[formFieldType] ?? []).length > 0;
}

/**
 * Dropdown options "Custom: <label>" for the custom fields a form field type
 * can feed. A typed answer can also feed a Nigerian state or LGA dropdown: the
 * backend matches it to the list ("lagos" → "Lagos") and skips it with a note
 * when it is not on the list.
 */
export function customFieldMapsToOptions(
  formFieldType: FormFieldType,
  fields: ReadonlyArray<Pick<CustomFieldFieldsFragment, 'key' | 'label' | 'type'> & { options_source?: CustomFieldOptionsSource | null }>,
): Array<{ label: string; value: string }> {
  const compatible = COMPATIBLE_CUSTOM_TYPES[formFieldType] ?? [];
  const typedIntoList = (f: { type: CustomFieldType; options_source?: CustomFieldOptionsSource | null }) =>
    formFieldType === FormFieldType.Text && f.type === CustomFieldType.Select && !!f.options_source;
  return fields
    .filter(f => compatible.includes(f.type) || typedIntoList(f))
    .map(f => ({ label: `Custom field: ${f.label}`, value: `${CUSTOM_FIELD_TARGET_PREFIX}${f.key}` }));
}
